import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { CheckCircle, ArrowRight, BookOpen, FileText, Shield, Award, Users, ClipboardCheck, Microscope, HelpCircle } from 'lucide-react';

const faqs = [
  {
    question: "What is the IMCAC certification examination process?",
    answer: "The certification process includes multiple assessment components: written examination (MCQ-based competency testing), OSCE (Objective Structured Clinical Examination), case log verification, viva voce examination, and ethics assessment. Candidates must pass all components to receive certification."
  },
  {
    question: "Who is eligible to take IMCAC certification examinations?",
    answer: "Eligibility requires valid medical or professional registration in your jurisdiction, completion of approved training or equivalent experience, and submission of required documentation including case logs. Specific requirements vary by certification type (CBEAM, CBELM, CBERM, etc.)."
  },
  {
    question: "How are IMCAC examinations different from academic degrees?",
    answer: "IMCAC certifications are competency-based professional credentials that validate practical clinical skills through rigorous assessment. They complement but do not replace academic qualifications or medical licensure. Certification confirms assessed competencies within specific clinical domains."
  },
  {
    question: "What happens if I fail one component of the examination?",
    answer: "Candidates who fail a component may retake that specific component without repeating passed sections (within the validity period). Detailed feedback is provided to guide preparation. Multiple attempts are permitted with applicable fees and waiting periods."
  },
  {
    question: "How long is IMCAC certification valid?",
    answer: "Initial certification is valid for 5 years. Recertification requires demonstration of continuing competency through CPD activities, case volume maintenance, and may include reassessment components. The Division provides clear guidelines for maintaining certified status."
  },
  {
    question: "Does IMCAC certification allow me to practice in any country?",
    answer: "No, IMCAC certification validates competency but does not grant medical licensure. Practitioners must hold valid professional registration in their jurisdiction and comply with local regulatory requirements. Certification may support applications but is not a substitute for local licensing."
  }
];

export default function ExamCertDivisionPage() {
  return (
    <div data-testid="exam-cert-division-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Core Division</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">
            Division of Competency-Based Examination & Certification
          </h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-3xl">
            An independent examination and certification authority operating under IMCAC, responsible for designing, 
            administering, and governing structured competency-based examinations for licensed healthcare professionals.
          </p>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="imcac-section bg-white">
        <div className="imcac-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <p className="imcac-overline mb-3">Mission Statement</p>
              <h2 className="text-3xl font-semibold text-[#1B2A4A] font-heading mb-6">
                Independent Examination Authority
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-6">
                To design, administer, and govern structured competency-based examinations for licensed healthcare 
                professionals and allied aesthetic personnel through standardised assessment frameworks. The Division 
                operates as an independent examination authority, ensuring impartial and rigorous certification standards.
              </p>

              <h3 className="text-xl font-semibold text-[#1B2A4A] font-heading mb-4">Core Functions</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {[
                  { icon: BookOpen, label: 'Written Examinations (MCQ-based competency testing)' },
                  { icon: ClipboardCheck, label: 'Objective Structured Clinical Examination (OSCE)' },
                  { icon: FileText, label: 'Case Log Verification' },
                  { icon: Users, label: 'Viva Voce & Case Defense' },
                  { icon: Shield, label: 'Ethics & Governance Assessment' },
                  { icon: Award, label: 'Certification Issuance' },
                  { icon: Microscope, label: 'Recertification & Continuing Competency' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-[#F8FAFC] rounded-sm border border-slate-100">
                    <item.icon className="h-4 w-4 text-[#C5A572] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <img
                src="https://customer-assets.emergentagent.com/job_cbeam-portal/artifacts/owme7yia_DSC_1416.JPG"
                alt="IMCAC Examination Division"
                className="w-full h-64 object-cover rounded-sm shadow-md mb-6"
              />
              <Card className="imcac-card border-t-2 border-t-[#C5A572]">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-3">Governance Statement</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    The Division operates independently from training providers to ensure examination integrity 
                    and impartial certification standards. No individual or institution involved in delivering 
                    IMCAC-accredited training programmes may participate in the assessment of their own graduates.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Faculty Sub-section */}
      <section className="imcac-section bg-[#F8FAFC]">
        <div className="imcac-container">
          <div className="text-center mb-12">
            <p className="imcac-overline mb-3">Under This Division</p>
            <h2 className="text-3xl font-semibold text-[#1B2A4A] font-heading">Faculties & Examinations</h2>
          </div>

          <Card className="imcac-card overflow-hidden mb-8" data-testid="faculty-card">
            <div className="bg-[#1B2A4A] px-6 md:px-8 py-6">
              <Badge className="bg-[#C5A572] text-white text-xs mb-2">Faculty</Badge>
              <h3 className="text-2xl font-bold text-white font-heading">Faculty of Aesthetic & Regenerative Medicine</h3>
              <p className="text-sm text-slate-300 mt-1">
                Oversees specialty-based competency assessment in aesthetic, longevity, and regenerative medical sciences.
              </p>
            </div>
            <CardContent className="p-6 md:p-8">
              <p className="text-base text-slate-600 leading-relaxed mb-6">
                The Faculty develops examination blueprints, OSCE frameworks, and certification standards aligned 
                with international competency models. It governs three specialty sections, each with its own 
                competency-based examination model.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {[
                  {
                    code: 'CBEAM',
                    title: 'Aesthetic Medicine',
                    fullName: 'Competency-Based Examination in Aesthetic Medicine',
                    to: '/faculty/aesthetic-medicine',
                  },
                  {
                    code: 'CBELM',
                    title: 'Longevity & Anti-Aging Medicine',
                    fullName: 'Competency-Based Examination in Longevity Medicine',
                    to: '/faculty/longevity-medicine',
                  },
                  {
                    code: 'CBERM',
                    title: 'Regenerative & Cellular Medicine',
                    fullName: 'Competency-Based Examination in Regenerative Medicine',
                    to: '/faculty/regenerative-medicine',
                  },
                ].map((exam, i) => (
                  <Card key={i} className="imcac-card border-t-2 border-t-[#C5A572]">
                    <CardContent className="p-5">
                      <Badge className="bg-[#1B2A4A] text-white text-xs mb-2">{exam.code}</Badge>
                      <h4 className="text-base font-semibold text-[#1B2A4A] font-heading mb-1">{exam.title}</h4>
                      <p className="text-xs text-slate-500 mb-4">{exam.fullName}</p>
                      <Link to={exam.to} data-testid={`explore-${exam.code.toLowerCase()}`}>
                        <Button variant="outline" className="w-full rounded-sm border-[#1B2A4A] text-[#1B2A4A] text-sm">
                          Explore {exam.code} <ArrowRight className="ml-1 h-3.5 w-3.5" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="text-center">
                <Link to="/faculty" data-testid="explore-faculty-btn">
                  <Button className="bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm px-8">
                    Explore Faculty <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className="py-10 bg-white">
        <div className="imcac-container">
          <div className="bg-[#F8FAFC] border border-slate-200 rounded-sm p-6">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-slate-600 leading-relaxed">
                <span className="text-[#C5A572] font-semibold">Legal Disclaimer:</span> Certification validates
                assessed competencies within defined domains and does not grant medical licensure. Candidates must
                hold valid professional registration in their jurisdiction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="imcac-section bg-slate-50">
        <div className="imcac-container max-w-4xl">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#1B2A4A]/5 rounded-sm mb-3">
              <HelpCircle className="h-3 w-3 text-[#C5A572]" />
              <span className="text-xs font-semibold tracking-wider text-[#1B2A4A] uppercase">FAQs</span>
            </div>
            <h2 className="text-2xl font-bold text-[#1B2A4A] font-heading">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-b border-slate-200">
                <AccordionTrigger className="text-left font-semibold text-[#1B2A4A] hover:text-[#C5A572] hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
